import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, InfiniteData } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, serverNow, ApiError, type MessageContent } from '../api';
import type { Link, LinkedMessage, Paginated } from '../api';
import { useAuth, useProcessMyUser } from '../store/auth';
import { useProcessMySession } from './useSessions';

type LinksPage = Paginated<Link, string>;
type LinksCache = InfiniteData<LinksPage>;

/**
 * Pure cache writer for the link inbox. Branches on `cutByMe`:
 *
 *   - `cutByMe: true` → I cut my side from another tab/device. The
 *     BE no longer surfaces this link to me, so drop it everywhere.
 *   - else → upsert in `['links','mine']` (re-sorted on activityAt
 *     desc) and update `['links', linkId]`.
 *
 * Single entry point for every link state change driven by the
 * `reloadYourLink` socket event + post-mutation handoffs.
 */
function rankMs(l: Link): number {
  return Number.isFinite(l.rankedAt) ? l.rankedAt : 0;
}

function sortLinksByRanked(list: Link[]): Link[] {
  return [...list].sort((a, b) => rankMs(b) - rankMs(a));
}

export function processMyLink(qc: QueryClient, link: Link) {
  processMyLinkList(qc, [link]);
}

/**
 * Batched flavor — apply N link updates with a single setQueryData on
 * `['links','mine']`. Per-id caches still get one write per link, but
 * their observers are scoped (link page / chat for that id), at most
 * one mounted.
 *
 * Each row branches on `cutByMe`: true → drop from inbox, mark per-id
 * with cutByMe (so the link page redirects), and remove its messages
 * cache. False → upsert in inbox + update per-id.
 */
export function processMyLinkList(qc: QueryClient, links: Link[]) {
  if (links.length === 0) return;

  qc.setQueryData<LinksCache>(['links', 'mine'], (old) => {
    if (!old?.pages.length) return old;
    // Flatten all loaded pages, apply each op, re-sort once, put it
    // back into the first page. Cursor pagination chains from the LAST
    // page's boundary id, so consolidating rows to page 0 is safe.
    let all = old.pages.flatMap(p => p.items);
    let touched = false;
    let needsSort = false;
    for (const link of links) {
      const i = all.findIndex(l => l.id === link.id);
      if (link.cutByMe) {
        if (i >= 0) {
          all = all.filter((l) => l.id !== link.id);
          touched = true;
        }
      } else {
        if (i >= 0) all = all.map((l, j) => (j === i ? { ...l, ...link } : l));
        else        all = [link, ...all];
        touched = true;
        needsSort = true;
      }
    }
    if (!touched) return old;
    if (needsSort) all = sortLinksByRanked(all);
    const [first, ...rest] = old.pages;
    return { ...old, pages: [{ ...first, items: all }, ...rest.map(p => ({ ...p, items: [] as Link[] }))] };
  });

  for (const link of links) {
    if (link.cutByMe) {
      // Mark per-id so the link page sees cutByMe=true and redirects,
      // independent of whether this came from HTTP or the socket.
      qc.setQueryData<Link>(['links', link.id], (old) =>
        old ? { ...old, cutByMe: true } : old,
      );
      qc.removeQueries({ queryKey: ['links', link.id, 'messages'] });
    } else {
      qc.setQueryData<Link>(['links', link.id], (old) =>
        old ? { ...old, ...link } : link,
      );
    }
  }
}

export function useProcessMyLink() {
  const qc = useQueryClient();
  return useCallback((link: Link) => processMyLink(qc, link), [qc]);
}

export function useProcessMyLinkList() {
  const qc = useQueryClient();
  return useCallback((links: Link[]) => processMyLinkList(qc, links), [qc]);
}


export function useMyLinks() {
  return useInfiniteQuery({
    queryKey: ['links', 'mine'],
    queryFn: ({ pageParam }) => api.links.mine({ next: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
  });
}

export function useLink(linkId: string) {
  return useQuery({
    queryKey: ['links', linkId],
    queryFn: () => api.links.get(linkId),
    enabled: !!linkId,
    // 404 means the BE has stopped surfacing the link to me (I cut
    // my side from another tab). Terminate immediately instead of
    // burning ~7s on default retries — the page redirects on error.
    retry: (_n, err) => !(err instanceof ApiError && err.status === 404),
  });
}

export function useCutLink() {
  const handle = useProcessMyLink();
  const handleUser = useProcessMyUser();
  return useMutation({
    mutationFn: (id: string) => api.links.cut(id),
    onSuccess: ({ link, user }) => {
      // Route through processMyLink — same path as the reloadYourLink
      // socket event. cutByMe=true → drop branch inside processMyLinkList
      // (removes inbox row, marks per-id with cutByMe so the link page
      // redirects to /messages).
      if (link) handle(link);
      // Bump countLinks (and any other denormalised user field) on the
      // originating tab. Other tabs receive the same payload via
      // `reloadYourUser` from the socket.
      handleUser(user);
    },
  });
}

export function useLinkMessages(linkId: string) {
  return useInfiniteQuery({
    queryKey: ['links', linkId, 'messages'],
    queryFn: ({ pageParam }) => api.links.getMessages(linkId, { next: pageParam }),
    initialPageParam: undefined as number | undefined,
    // Older messages live in the "next" direction (paginate backward
    // in time); BE returns the cursor to use ready in `next`.
    getPreviousPageParam: (firstPage) => firstPage.next ?? undefined,
    getNextPageParam: () => undefined,
    enabled: !!linkId,
    // Disable every form of TanStack auto-refetch. Older pages already
    // loaded via fetchPreviousPage are immutable history, no point
    // re-fetching them. New messages catch-up uses an explicit
    // `?prev=<max>` flow in the chat page, not TanStack's refetch.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

type LinkMsgPage = { messages: LinkedMessage[]; next: number | null; prev: number | null };
type LinkedMsgCache = { pages: LinkMsgPage[]; pageParams: unknown[] };

/**
 * Optimistic message send for permanent link chats. Mirrors the
 * session-side flow: append a `_pending` placeholder before the
 * request leaves, swap to the real row on success, drop on failure.
 * The mutation response now carries `{ link, message }` — link is
 * routed through processMyLink so the inbox card bumps activityAt
 * on the originating tab without a refetch.
 */
export function useSendLinkMessage(linkId: string) {
  const qc = useQueryClient();
  const userId = useAuth(s => s.user?.id);
  const handle = useProcessMyLink();
  return useMutation({
    mutationFn: (content: MessageContent) => api.links.sendMessage(linkId, content),

    onMutate: async (content) => {
      await qc.cancelQueries({ queryKey: ['links', linkId, 'messages'] });
      const tempId = `temp:${Math.random().toString(36).slice(2, 10)}:${Date.now()}`;
      const optimistic: LinkedMessage = {
        id: tempId,
        linkId,
        senderId: userId ?? '',
        content,
        createdAt: serverNow() * 1000,
        _pending: true,
      };
      qc.setQueryData<LinkedMsgCache>(['links', linkId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const pages = old.pages.slice();
        const last = pages[pages.length - 1];
        pages[pages.length - 1] = { ...last, messages: [...last.messages, optimistic] };
        return { ...old, pages };
      });
      return { tempId };
    },

    onSuccess: ({ link, message }, _vars, ctx) => {
      handle(link);
      qc.setQueryData<LinkedMsgCache>(['links', linkId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const tempId = ctx?.tempId;
        let touched = false;
        const pages = old.pages.map(p => {
          const idx = tempId ? p.messages.findIndex(m => m.id === tempId) : -1;
          if (idx >= 0) {
            touched = true;
            const msgs = p.messages.slice();
            msgs[idx] = message;
            return { ...p, messages: msgs };
          }
          return p;
        });
        if (!touched) {
          const last = pages[pages.length - 1];
          pages[pages.length - 1] = { ...last, messages: [...last.messages, message] };
        }
        return { ...old, pages };
      });
    },

    onError: (_err, _vars, ctx) => {
      const tempId = ctx?.tempId;
      if (!tempId) return;
      qc.setQueryData<LinkedMsgCache>(['links', linkId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const pages = old.pages.map(p => ({ ...p, messages: p.messages.filter(m => m.id !== tempId) }));
        return { ...old, pages };
      });
    },
  });
}

/**
 * Photo-message variant of `useSendLinkMessage`. The optimistic bubble
 * uses an in-memory `blob:` URL so the user sees their own image right
 * away — revoked once the server confirms with the real S3 URL (or
 * fails). Same temp-id dance as the text mutation; the only twist is
 * the `URL.revokeObjectURL` cleanup in onSuccess/onError so we don't
 * leak object URLs across multiple sends in the same chat.
 */
export function useSendLinkPhoto(linkId: string) {
  const qc = useQueryClient();
  const userId = useAuth(s => s.user?.id);
  const handle = useProcessMyLink();
  return useMutation({
    mutationFn: (file: File) => api.links.sendPhoto(linkId, file),

    onMutate: async (file) => {
      await qc.cancelQueries({ queryKey: ['links', linkId, 'messages'] });
      const tempId = `temp:${Math.random().toString(36).slice(2, 10)}:${Date.now()}`;
      const blobUrl = URL.createObjectURL(file);
      const optimistic: LinkedMessage = {
        id: tempId,
        linkId,
        senderId: userId ?? '',
        // Optimistic content uses the blob URL for both `url` and
        // `thumbUrl` — the bubble picks the first one that loads.
        // Width/height are unknown until the file decodes; the bubble
        // renders without intrinsic sizing for the optimistic frame.
        content: { type: 'image', url: blobUrl, thumbUrl: blobUrl },
        createdAt: serverNow() * 1000,
        _pending: true,
      };
      qc.setQueryData<LinkedMsgCache>(['links', linkId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const pages = old.pages.slice();
        const last = pages[pages.length - 1];
        pages[pages.length - 1] = { ...last, messages: [...last.messages, optimistic] };
        return { ...old, pages };
      });
      return { tempId, blobUrl };
    },

    onSuccess: ({ link, message }, _vars, ctx) => {
      handle(link);
      qc.setQueryData<LinkedMsgCache>(['links', linkId, 'messages'], (old) => {
        if (!old?.pages.length) return old;
        const tempId = ctx?.tempId;
        let touched = false;
        const pages = old.pages.map(p => {
          const idx = tempId ? p.messages.findIndex(m => m.id === tempId) : -1;
          if (idx >= 0) {
            touched = true;
            const msgs = p.messages.slice();
            msgs[idx] = message;
            return { ...p, messages: msgs };
          }
          return p;
        });
        if (!touched) {
          const last = pages[pages.length - 1];
          pages[pages.length - 1] = { ...last, messages: [...last.messages, message] };
        }
        return { ...old, pages };
      });
      if (ctx?.blobUrl) URL.revokeObjectURL(ctx.blobUrl);
    },

    onError: (_err, _vars, ctx) => {
      const tempId = ctx?.tempId;
      if (tempId) {
        qc.setQueryData<LinkedMsgCache>(['links', linkId, 'messages'], (old) => {
          if (!old?.pages.length) return old;
          const pages = old.pages.map(p => ({ ...p, messages: p.messages.filter(m => m.id !== tempId) }));
          return { ...old, pages };
        });
      }
      if (ctx?.blobUrl) URL.revokeObjectURL(ctx.blobUrl);
    },
  });
}

export function useSendLinkRequest() {
  const handleSession = useProcessMySession();
  return useMutation({
    mutationFn: (sessionId: string) => api.sessions.sendRequest(sessionId),
    onSuccess: (session) => { if (session) handleSession(session); },
  });
}

export function useRespondLinkRequest() {
  const handleSession = useProcessMySession();
  const handleLink = useProcessMyLink();
  const handleUser = useProcessMyUser();
  return useMutation({
    mutationFn: ({ sessionId, accept }: { sessionId: string; accept: boolean }) =>
      api.sessions.respondRequest(sessionId, accept),
    onSuccess: ({ session, link, user }) => {
      if (session) handleSession(session);
      if (link) handleLink(link);
      // Only the accept path returns `user` (reject doesn't change
      // counts). When present, treat it like the cut response — the
      // originating tab updates its store, other tabs catch up via
      // the `reloadYourUser` socket event.
      if (user) handleUser(user);
    },
  });
}

/**
 * Caller-perspective unread check: the BE projects `lastMessageAt`
 * (other side's latest send) and `lastReadAt` (this side's watermark)
 * into the `Link` payload, so the FE just compares.
 */
export function linkHasUnread(link: Link): boolean {
  return link.lastMessageAt > link.lastReadAt;
}


export function useRevokeLinkRequest() {
  const handleSession = useProcessMySession();
  return useMutation({
    mutationFn: (sessionId: string) => api.sessions.revokeRequest(sessionId),
    onSuccess: (session) => { if (session) handleSession(session); },
  });
}
